import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import loadable from '@loadable/component';
import _ from 'lodash';
import { Redirect, useHistory } from 'react-router-dom';
import ProductDetailMeta from './ProductDetailMeta';
import LocationSelector from './LocationSelector';
import ProductImages from './ProductImages';
import QuantityBox from './QuantityBox';
import QuantityInput from './QuantityInput';
import VariantSelector from './VariantSelector';
import useSWR from 'swr';

// import DealerInfo from '../../components/DealerInfo/DealerInfo';
import MoreProducts from '../../components/MoreProducts/MoreProducts';
import Meisterkachel from '../../components/Meisterkachel/Meisterkachel';
import BreadCrumbSelector from '../../components/BreadCrumb/BreadCrumbSelector';
import ReduxSuspense from '../../components/ReduxSuspense/ReduxSuspense';
import { productResponseSelector } from '../../../modules/selectors/standort/vehicleFilter/product';
import { fetchProductIfNeeded, setProductVariantId } from '../../../modules/actions/standort/product';
import { ExternalLink } from '../../components/form-elements/Link';
import withStandortRouter from '../../components/VHost/withStandortRouter';
import { standortUrlSelector } from '../../../modules/selectors/url';
import { addProduct } from '../../../modules/actions/standort/session/cart';
import TireInformationModal from '../../components/Tires/TireInformationModal';
import TireEnergyLabelLinks from '../Tires/TireEnergyLabelLinks';
import { datECodePrefixSelector } from '../../../modules/selectors/vehicle';
import { markenStandortSelector } from '../../../modules/selectors/standort/markenStandort';
import Status from '../../components/Status/Status';
import StandortRedirect from '../../components/VHost/StandortRedirect';
import { toast } from '../../../helpers/toastHelper';
import Spinner from '../../components/Spinner/Spinner';
import { saveproduct, resetproduct } from '../../../modules/actions/productID';
import Button from '../../components/form-elements/Button';
import { trackEvent } from '../../components/Tracking/Matomo';
import { getActiveSalesPrice } from '../../../helpers/productHelper';
import { apiClient } from '../../../helpers/apiHelperV2'; 
import classnames from 'classnames';

const ServiceStationFinder = loadable(() => import('../../components/ServiceStationFinder/ServiceStationFinder'));

function ProductDetail({ product, categoryUrl, productUrl, isExact }) {
    const { selectedVariantId } = product;
    const headline = useRef(null);

    const dispatch = useDispatch();
    const markenStandort = useSelector(markenStandortSelector);
    const standortUrl = useSelector(standortUrlSelector);
    const datECodePrefix = useSelector(datECodePrefixSelector);
    const history = useHistory();

    const [plz, setPlz] = useState('');
    const [showMap, setShowMap] = useState(false);
    const serviceStationFinderRef = useRef(null);
    const [selectedQuantityBox, setSelectedQuantityBox] = useState(product.selectedLieferweg);
    const [quantityValue, setQuantityValue] = useState(1);

    const variant = useMemo(() => {
        if (!_.isEmpty(product.varianten)) {
            return product.varianten.find(v => v._id === selectedVariantId) || product.varianten[0];
        }
        return undefined;
    }, [product, selectedVariantId]);

    if (variant.marketingText.includes('https://media0.autoteileplusservice.de')) {
        variant.marketingText = variant.marketingText.replace('https://media0.autoteileplusservice.de', 'https://techno-images.s3.eu-central-1.amazonaws.com')
    }

    const soldOut = useMemo(() => {
        if (!variant) return false;

        return (
            (variant.isOwnArticle && variant.stockkeeping && variant.stock < 1 && variant.stock !== null) ||
            (!variant.isOwnArticle && variant.stock < 1) ||
            variant.soldOut
        );
    }, [variant]);


    const deliveryTime = useMemo(() => {
        if (!variant) return false;

        const { deliveryDaysFrom, deliveryDaysTo } = variant;

        if (deliveryDaysFrom > 0 && deliveryDaysTo > 0) {
            return `${deliveryDaysFrom}-${deliveryDaysTo} Werktage`;
        }

        if (deliveryDaysFrom === 1) {
            return `${deliveryDaysFrom} Werktag`;
        } else if (deliveryDaysFrom > 0) {
            return `${deliveryDaysFrom} Werktage`;
        }


        if (deliveryDaysTo === 1) {
            return `${deliveryDaysTo} Werktag`;
        } else if (deliveryDaysTo > 0) {
            return `${deliveryDaysTo} Werktage`;
        }

        return '3-5 Werktage';

    }, [variant]);

    const pickupTime = useMemo(() => {
        if (!variant) return false;

        const { pickupDaysFrom, pickupDaysTo } = variant;

        if (pickupDaysFrom > 0 && pickupDaysTo > 0) {
            return `${pickupDaysFrom}-${pickupDaysTo} Werktagen`;
        }

        if (pickupDaysFrom === 1) {
            return `${pickupDaysFrom} Werktag`;
        } else if (pickupDaysFrom > 0) {
            return `${pickupDaysFrom} Werktagen`;
        }

        if (pickupDaysTo === 1) {
            return `${pickupDaysTo} Werktag`;
        } else if (pickupDaysTo > 0) {
            return `${pickupDaysTo} Werktagen`;
        }

        return '3-5 Werktagen';

    }, [variant]);

    const setVariantId = useCallback(
        variantId =>
            dispatch(
                setProductVariantId({
                    standortUrl,
                    datECodePrefix,
                    categoryUrl,
                    productUrl,
                    variantId,
                })
            ),
        [categoryUrl, datECodePrefix, dispatch, productUrl, standortUrl]
    );
    useEffect(() => {
        if (showMap) {
            // TODO: refactor scroll centering of freshly mounted child DOM nodes
            serviceStationFinderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [showMap]);

    useEffect(() => {
        if (variant) {
            let productID;
            if (variant.isOwnArticle) {
                productID = variant.shownId || variant._id;
            } else {
                productID = variant.id;
            }
            dispatch(saveproduct(productID));
        }
    }, [dispatch, variant]);

    useEffect(() => {
        const unlisten = history.listen((location, action) => {
            dispatch(resetproduct());
        });

        return unlisten;
    }, [dispatch, history]);

    useEffect(() => {
        if (standortUrl === "gottfried-schultz") {
            const script = document.createElement('script');
            script.src = '//cdn.doofinder.com/recommendations/js/doofinderRecommendation.min.js';
            script.async = true;
            document.body.appendChild(script);
            return () => {
                document.body.removeChild(script);
            };
        }
        
        if (headline.current) {
            headline.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        
    }, [standortUrl])


    if (product.redirect) {
        toast({ message: product.redirect.message, status: 'warning' });
        return <StandortRedirect to={`/${categoryUrl}`} />;
    }

    if (_.isEmpty(product.varianten) || !variant) {
        // Invalid state
        return (
            <Status code={500}>
                <section className="uk-section uk-section-default uk-section-small">
                    <div className="uk-container" data-uk-margin="">
                        Es ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut.
                    </div>
                </section>
            </Status>
        );
    }

    //const vorteile = product.selectedLieferweg?.Vorteilskommunikation ?? [];
    const additionalCrumbs = [
        {
            label: product.isOwnArticle ? variant.slug ?? 'Artikel' : variant.slug ?? product.id,
            key: categoryUrl
        },
    ];
    const isOil = product.kennzeichnung === 'O' || variant.dangerous === 'oil' || variant.articleType === 'öl';
    const isBattery = product.kennzeichnung === 'B' || variant.dangerous === 'battery' || variant.articleType === 'batterie';
    let isOfferActive = variant.isDeliveryOfferActive || variant.isPickUpOfferActive;
    const lieferant = product?.lieferantenContact && product?.lieferantenContact.length > 0 ? product?.lieferantenContact[0] : false
    const herstellerangaben = variant?.herstellerangaben ? variant?.herstellerangaben : null;

    const handleQuantityInputChange = (newValue) => {
        setQuantityValue(newValue);
    };

    const handleAddToCart = (buttonType) => {
        if (!_.isNil(variant.stock) && variant.stock < quantityValue) {
            window.UIkit.notification(`Maximale Bestellmenge: ${variant.stock}`, {
                status: 'danger',
                pos: 'top-right',
            });
            return
        }
        trackEvent({
            category: 'E-Commerce',
            action: 'Lege-in-Warenkorb',
            label: variant.id || (product.shownId || product.id),
            value: getActiveSalesPrice(variant) * quantityValue * 100,
        });
        dispatch(addProduct({ product: { ...variant, selectedLieferweg: selectedQuantityBox, deliveryDays: selectedQuantityBox.legalType === 'online-kauf' ? deliveryTime : pickupTime, deliveryText: selectedQuantityBox.legalType === 'online-kauf' ? `Lieferzeit: ${deliveryTime}` : `Abholbereit in: ${pickupTime}`  }, quantity: quantityValue, buttonType }));
    }

    const { data: seitenTexteResponse } = useSWR(`/api/v1/SeitenTexte?standortUrl=${markenStandort.url}`, async url => {
        const { data } = await apiClient.get(url);
        return data;
    });

    const seitenTexte = useMemo(() => seitenTexteResponse?.body ?? [], [seitenTexteResponse]);
    const textBatterie = (seitenTexte && seitenTexte.find(text => text.name === 'Batterie')) || undefined;
    const textAltöl = (seitenTexte && seitenTexte.find(text => text.name === 'Altöl')) || undefined;

    return (
        <>
            <BreadCrumbSelector additionalCrumbs={additionalCrumbs} />
            <ProductDetailMeta variant={variant} soldOut={soldOut} isExact={isExact} />
            <section className="uk-section uk-section-default uk-section-small">
                <div className="uk-container uk-margin-medium-top" data-uk-margin="">
                    {/* {soldOut && (
                        <div
                            className="uk-light uk-border-pill uk-padding-small uk-width-medium uk-text-center uk-text-bold uk-text-large"
                            style={{ background: 'var(--grey-light-main, #e0e0e0)', color: 'var(--font-main)'}}
                        >
                            In Kürze wieder lieferbar
                        </div>
                    )} */}
                    <div className="uk-grid uk-child-width-1-1 uk-child-width-1-2@s uk-child-width-1-2@m uk-first-column" data-uk-grid="">
                        <div className="product-images-container">
                            <ProductImages variant={variant} images={variant.images ?? []} isOfferActive={isOfferActive} />
                        </div>
                        <div className="product-info-container" ref={headline}>
                            <div className='uk-container'>
                                <h1 className='headline'>
                                    <span dangerouslySetInnerHTML={{ __html: variant.bezeichnung }} />
                                </h1>
                                <p>Artikelnummer: {variant.shownId || variant.id}</p>
                                <VariantSelector product={product} variantId={selectedVariantId} setVariantId={setVariantId} />
                                {/*  for lieferzeit */}
                                <div className="uk-flex uk-flex-between uk-flex-middle uk-padding-small uk-padding-remove-top uk-padding-remove-left uk-padding-remove-right">
                                    {(!variant.soldOut && deliveryTime && selectedQuantityBox.legalType === 'online-kauf') && (
                                        <>
                                            <div className="uk-flex uk-flex-middle">
                                                <span className="uk-icon uk-icon-circle uk-text-success lieferzeit-icon-badge"></span>

                                                <span className="uk-text-small uk-margin-small-left">Lieferzeit {deliveryTime}</span>
                                            </div>
                                        </>
                                    )}
                                    {!variant.soldOut && pickupTime && selectedQuantityBox.legalType === "reservierung" && (
                                        <>
                                            <div className="uk-flex uk-flex-middle">
                                                <span className="uk-icon uk-icon-circle uk-text-success lieferzeit-icon-badge"></span>

                                                <span className="uk-text-small uk-margin-small-left">Abholbereit innerhalb von {pickupTime}</span>
                                            </div>
                                        </>
                                    )}
                                    {(variant.freeShipping || variant.shippingCostPerArticle === 0) && (
                                        <>
                                            {(product.selectedLieferweg.legalType === 'online-kauf') && (
                                                <span className="uk-text-small lieferzeit-text">versandkostenfrei</span>
                                            )}
                                        </>
                                    )}

                                </div>
                            </div>
                            <div className="uk-child-width-1-1 uk-grid-small" data-uk-grid="">
                                {(!_.isNil(variant.vk_preis) || !_.isNil(variant.pickup_preis)) ? (
                                    <>
                                        <QuantityBox
                                            isBattery={isBattery}
                                            lieferweg={product.selectedLieferweg}
                                            variant={variant}
                                            isOfferActive={product.selectedLieferweg.legalType === 'online-kauf' ? variant.isDeliveryOfferActive : variant.isPickUpOfferActive}
                                            isSelected={selectedQuantityBox === product.selectedLieferweg}
                                            onSelect={() => setSelectedQuantityBox(product.selectedLieferweg)}
                                            quantity={quantityValue}
                                        />
                                        {(product.additionalPickupLieferweg && variant.additionalPickup) && (
                                            <QuantityBox
                                                isBattery={isBattery}
                                                lieferweg={product.additionalPickupLieferweg}
                                                variant={variant}
                                                isOfferActive={variant.isPickUpOfferActive}
                                                isSelected={selectedQuantityBox === product.additionalPickupLieferweg}
                                                onSelect={() => setSelectedQuantityBox(product.additionalPickupLieferweg)}
                                                quantity={quantityValue}
                                            />
                                        )}
                                    </>
                                ) : (
                                    !showMap && (
                                        <LocationSelector
                                            onSubmit={({ zipCode }) => {
                                                setPlz(zipCode);
                                                setShowMap(true);
                                            }}
                                        />
                                    )
                                )}
                                <div className='uk-grid-small uk-flex-middle uk-padding-remove uk-margin-auto' data-uk-grid="">
                                    <div className="uk-width-1-2@l uk-width-auto uk-flex uk-flex-left@s">
                                        {selectedQuantityBox.abcKategorie === 'C' &&
                                            <div className="uk-form-controls">
                                                <QuantityInput initialValue={1} minValue={1} maxValue={4} onChange={handleQuantityInputChange} />
                                            </div>
                                        }
                                        {selectedQuantityBox.abcKategorie !== 'C' &&
                                            <div className="uk-form-controls">
                                                <QuantityInput initialValue={1} minValue={1} maxValue={(!variant.stock || variant.stock > 999) ? 1000 : variant.stock} onChange={handleQuantityInputChange} />
                                            </div>
                                        }
                                    </div>
                                    <div className="uk-width-1-3 uk-flex uk-flex-column uk-text-center uk-text-right@s uk-width-expand uk-margin-remove-top@s">
                                        <Button
                                            className={classnames('custom-button-qualityBox uk-text-uppercase', { 'soldout-theme': soldOut })}
                                            type="button"
                                            disabled={soldOut}
                                            onClick={() => handleAddToCart(selectedQuantityBox.legalType === 'online-kauf' ? 'addToCart' : 'reserve')}>
                                            {selectedQuantityBox.legalType === 'online-kauf' ? 'Zum Warenkorb hinzufügen' : 'RESERVIEREN & ABHOLEN'}
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    {(selectedQuantityBox.abcKategorie === 'C') && (
                                        <div className="uk-text-small" style={{ color: 'var(--shop-main)' }}>
                                            Max. Bestellmenge: 4
                                        </div>
                                    )}
                                    {(selectedQuantityBox.abcKategorie !== 'C' && variant.stockkeeping &&  variant.stock > 0) && (
                                        <div className="uk-text-small" style={{ color: 'var(--shop-main)' }}>
                                            Max. Bestellmenge: {variant.stock}
                                        </div>
                                    )}
                                </div>
                                {/* Variant and for reifen articles */}

                                {variant.articleType === 'reifen' && (
                                    <div>
                                        <div className="uk-margin-small-bottom">
                                            <TireInformationModal tire={variant} />
                                        </div>
                                        <div data-uk-grid="" className="uk-grid-small uk-child-width-expand@s">
                                            <TireEnergyLabelLinks tire={variant} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {herstellerangaben && 
                        <div className='uk-margin-large-bottom'>
                            <span className='uk-text-bold'>Herstellerangaben: </span>
                            <span>
                                {herstellerangaben?.firma ? `${herstellerangaben?.firma} | ` : ""} 
                                {herstellerangaben?.street ? `${herstellerangaben?.street} | ` : ""} 
                                {herstellerangaben?.city || herstellerangaben.zipCode ? `${herstellerangaben?.zipCode ?? ''} ${herstellerangaben?.city ?? ''} | ` : ""} 
                                {herstellerangaben?.phone ? `Tel: ${herstellerangaben.phone} | ` : ""} 
                                {herstellerangaben?.email ? <>E-Mail: <ExternalLink to={`mailto:${herstellerangaben.email}`}>{herstellerangaben.email}</ExternalLink> | </> : ""} 
                                {herstellerangaben?.homepage ? <>Webseite: <ExternalLink to={`${herstellerangaben.homepage}`}>{herstellerangaben.homepage}</ExternalLink> </> : ""}    
                            </span>
                        </div>
                    }
                    
                    {lieferant && 
                        <div className='uk-margin-large-bottom'>
                            <span className='uk-text-bold'>Herstellerangaben:</span><br/>
                            <span>
                                {lieferant?.name ? `${lieferant?.name} | ` : ""} 
                                {lieferant?.street ? `${lieferant?.street} | ` : ""} 
                                {lieferant?.city && lieferant.zipCode ? `${lieferant?.zipCode} ${lieferant?.city} | ` : ""} 
                                {lieferant?.phone ? `Tel: ${lieferant.phone} | ` : ""} 
                                {lieferant?.fax ? `Fax: ${lieferant.fax} | ` : ""} 
                                {lieferant?.email ? <>E-Mail: <ExternalLink to={`mailto:${lieferant.email}`}>{lieferant.email}</ExternalLink> | </> : ""} 
                                {lieferant?.homepage ? `Webseite: ${lieferant.homepage}` : ""}  
                            </span>
                        </div>
                    }

                    {
                    variant.marketingText && 
                    variant.marketingText !== "" && 
                    variant.marketingText !== "undefined" && (
                        <div>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: variant.marketingText,
                                }}
                            />
                        </div>
                    )}
                    {variant.dangerousGoodDescription && (
                        <div>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: variant.dangerousGoodDescription,
                                }}
                            />
                        </div>
                    )}
                    {variant.sicherHeitsDatenblatt && (
                        <div className="uk-width-1-1 uk-text-large uk-text-right">
                            <ExternalLink to={`https://www.tibsonline.de/AddvMultimedia/${variant.sicherHeitsDatenblatt}`}>
                                <i className="fa fa-file-pdf-o uk-margin-small-right" />
                                Sicherheitsdatenblatt
                            </ExternalLink>
                        </div>
                    )}
                    {/* {isOil && (
                        <div className="uk-width-1-1 uk-text-large uk-text-right">
                            <InternalLink to="/altoelHinweis" newTab>
                                <i className="fa fa-file-text-o uk-margin-small-right" />
                                Hinweis zur Entsorgung von Altöl
                            </InternalLink>
                        </div>
                    )}
                    {isBattery && (
                        <div className="uk-width-1-1 uk-text-large uk-text-right">
                            <ExternalLink to="https://techno-images.s3.eu-central-1.amazonaws.com/pdfs/EntsorgungBatterie2.pdf">
                                <i className="fa fa-file-pdf-o uk-margin-small-right" />
                                Hinweis zur Batterieentsorgung
                            </ExternalLink>
                        </div>
                    )} */}
                    {variant.articleType === 'batterie' && (
                        <div className="uk-width-1-1 uk-text-small uk-background-muted uk-padding-small uk-margin-medium-top">
                            {textBatterie && <div dangerouslySetInnerHTML={{ __html: textBatterie.text }} />}
                        </div>
                    )}
                    {variant.articleType === 'öl' && (
                        <div className="uk-width-1-1 uk-text-small uk-background-muted uk-padding-small uk-margin-medium-top">
                            {textAltöl && <div dangerouslySetInnerHTML={{ __html: textAltöl.text }} />}
                            <p>                                               
                                <strong>                         
                                    {`${markenStandort.anschrift.firma} | ${markenStandort.anschrift.strasse} | ${markenStandort.anschrift.plz} ${markenStandort.anschrift.ort}`}
                                </strong>{' '}
                                <br />
                                <br />
                                Sie können das Altöl dort jederzeit kostenlos während unserer Öffnungszeiten: <br />
                                <br /> <strong>{markenStandort.oeffnungszeiten}</strong> abgeben. <br /> <br />
                                Es besteht auch die Möglichkeit, das Altöl an uns zurückzusenden. Bitte beachten Sie, 
                                dass für das Versenden besondere Transportbedingungen, insbesondere die Bestimmungen zum 
                                Gefahrgut gelten können; bitte holen Sie vor der Versendung entsprechende Informationen ein. 
                                Nehmen Sie auch zur Kenntnis, dass wir die Kosten der Rücksendung von Altöl nicht übernehmen. 
                                Unsere Annahmestelle verfügt über eine Einrichtung, die es ermöglicht, den Ölwechsel fachgerecht durchzuführen.
                            </p>
                        </div>
                    )}
                    {variant.documents?.length > 0 && (
                        <ul className="uk-list">
                            {variant.documents
                                .filter(document => !_.isString(document))
                                .map(({ url, description }, i) => {
                                    return (
                                        <li key={i}>
                                            <ExternalLink to={url} modifier="reset">
                                                <i className="fa fa-file-pdf-o uk-margin-small-right" />
                                                {description}
                                            </ExternalLink>
                                        </li>
                                    );
                                })}
                        </ul>
                    )}
                    {/* {vorteile.length > 0 && (
                        <div className="uk-background-muted uk-padding">
                            <div className="uk-flex uk-flex-wrap uk-flex-center@m">
                                {vorteile.map((vorteil, i) => (
                                    <div className="uk-flex uk-flex-middle uk-margin-small-right uk-width-1-1 uk-width-auto@m" key={i}>
                                        <i className="fa fa-check uk-margin-small-right uk-font-main" />
                                        {vorteil?.text ? vorteil?.text : vorteil}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )} */}
                    <div ref={serviceStationFinderRef} className="serviceStationFinderRef">
                        {showMap && (
                            <ServiceStationFinder
                                prefilledZip={plz}
                                categoryUrl={categoryUrl}
                                // Link to the currently selected variant exactly
                                redirectUrl={`/${categoryUrl}/${variant.slug || variant.id || variant._id}/exakt`}
                                productVariantId={variant._id}
                            />
                        )}
                    </div>
                    {/* {isBattery && (
                        <div>
                            * Im Zusammenhang mit dem Vertrieb von Fahrzeugbatterien sind wir gemäß § 10 BattGh verpflichtet, je
                            Fahrzeugbatterie ein Pfand in Höhe von 7,50 Euro einschließlich Umsatzsteuer zu erheben. Dies entfällt, wenn Sie
                            beim Kauf Ihre gebrauchte Batterie bei uns entsorgen.
                        </div>
                    )} */}
                </div>
            </section>
            {(standortUrl === "gottfried-schultz") && (
                  <section className="uk-section uk-section-default uk-section-small">
                    <div className="uk-container" data-uk-margin="">
                      <df-recommendations
                          hashid="620d82bb1f2780b7e65724b6536633bb"
                          total-products="10"
                      ></df-recommendations>
                    </div>
                  </section>
            )}
            <MoreProducts />
            {/* {!markenStandort.isTechno && <DealerInfo />} */}
            <section className="uk-section uk-section-muted uk-section-small">
                <div className="uk-container uk-container-xsmall">
                    <Meisterkachel />
                </div>
            </section>
        </>
    );
}

function LoadProductDetail({ categoryUrl, productUrl, productResponse, isExact }) {
    const markenStandort = useSelector(markenStandortSelector);
    let url = `/${markenStandort.url}`;
    if (markenStandort.url !== 'de') {
        url = '';
    }
    return (productResponse.status !== "FAILURE" ? (
        <ReduxSuspense
            response={productResponse}
            fallback={
                <>
                    <BreadCrumbSelector />
                    <Spinner />
                </>
            }
        >
            {product => <ProductDetail product={product} categoryUrl={categoryUrl} productUrl={productUrl} isExact={isExact} />}
        </ReduxSuspense>
    ) :

        (<Redirect to={`${url}/${categoryUrl}`} />)

    );
}

const frontload = async ({ fetchProductIfNeeded, productUrl, categoryUrl, isExact }) => {
    await fetchProductIfNeeded({ productUrl, categoryUrl, isExact });
};

const mapStateToProps = (state, { match }) => {
    const { categoryUrl, productUrl, exakt } = match.params;
    return {
        productResponse: productResponseSelector(state, { productUrl, categoryUrl }),
        isExact: exakt === 'exakt',
        categoryUrl,
        productUrl,
    };
};

const mapDispatchToProps = { fetchProductIfNeeded, addProduct };

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStandortRouter(frontloadConnect(frontload, { onUpdate: true })(LoadProductDetail)));
